import { QuestionModel } from '@/modules/core/domain/interfaces/IFormulary';
import { formularyStorage } from '@/modules/shared/adapters/CloudStorage';
import useField from '@/modules/WebForm/hooks/useField';
import { createAnswer } from '@/modules/WebForm/utils';
import {
  Button,
  Divider,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { ChangeEventHandler } from 'react';
import {
  BiChevronDown,
  BiLinkAlt,
  BiMicrophone,
  BiTrashAlt,
} from 'react-icons/bi';

import FormControlComponent from '../FormControlComponent';
import AudioRecorderModal from './AudioRecorderModal';

const AudioContent: React.FC<QuestionModel> = () => {
  const { onChange, fieldState, error } = useField();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [isLoading, setLoading] = React.useState(false);
  const { onOpen, onClose, isOpen } = useDisclosure();

  const value = React.useMemo(() => {
    if (!Array.isArray(fieldState)) return fieldState;

    return undefined;
  }, [fieldState]);

  const handleChange = React.useCallback(
    (downloadUrl: string) => {
      onChange(createAnswer(downloadUrl));
    },
    [onChange],
  );

  const handleRemove = React.useCallback(() => {
    onChange(undefined as any);
  }, [onChange]);

  const handleConfirm = React.useCallback(
    async (file: File) => {
      try {
        setLoading(true);
        await formularyStorage.uploadFile({
          file,
          setFileDownloadUrl: (downloadUrl) => {
            handleChange(downloadUrl);
            setLoading(false);
            onClose();
          },
        });
      } catch (e) {
        setLoading(false);
      }
    },
    [handleChange, onClose],
  );

  const handlePickFile: ChangeEventHandler<HTMLInputElement> = React.useCallback(
    (event) => {
      const file = event?.target?.files?.[0];
      if (file) {
        handleConfirm(file);
      }
    },
    [handleConfirm],
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <>
      <AudioRecorderModal
        isLoading={isLoading}
        onConfirm={handleConfirm}
        isOpen={isOpen}
        onClose={onClose}
      />
      <FormControlComponent isInvalid={error}>
        <VStack spacing="6" alignItems="flex-start">
          <input
            type="file"
            multiple={false}
            hidden
            accept="audio/*"
            ref={inputRef}
            onChange={handlePickFile}
          />
          {value?.content ? (
            <VStack alignItems="flex-start" w="full">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio controls src={value.content} style={{ width: '100%' }} />
              <HStack>
                <Button
                  onClick={onOpen}
                  size="xs"
                  colorScheme="brand"
                  rightIcon={<BiMicrophone size={18} />}
                >
                  Gravar novamente
                </Button>
                <Button
                  onClick={handleRemove}
                  size="xs"
                  rightIcon={<BiTrashAlt size={18} />}
                >
                  Remover
                </Button>
              </HStack>
              <Divider />
            </VStack>
          ) : (
            <Wrap>
              <WrapItem alignItems="center">
                <Menu>
                  {({ isOpen: isMenuOpen }) => (
                    <>
                      <MenuButton
                        size="sm"
                        isActive={isMenuOpen}
                        as={Button}
                        isLoading={isLoading}
                        colorScheme="brand"
                        rightIcon={<BiChevronDown />}
                      >
                        Adicionar áudio
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          onClick={handleClick}
                          icon={<BiLinkAlt size={18} />}
                        >
                          Anexar
                        </MenuItem>
                        <MenuItem
                          onClick={onOpen}
                          icon={<BiMicrophone size={18} />}
                        >
                          Gravar áudio
                        </MenuItem>
                      </MenuList>
                    </>
                  )}
                </Menu>
              </WrapItem>
            </Wrap>
          )}
        </VStack>
      </FormControlComponent>
    </>
  );
};

export default AudioContent;
